/* ET - Cart Sticky Bar — reveal the bar once the shopper has scrolled past a configurable point.

   The running total and checkout then stay within reach for the rest of the page, which on mobile is most of it:
   below the items sit the summary, the testimonials slider and the recommendations slider. How early that happens
   is the section's "Show the bar after" setting - see the modes below.

   Progressive enhancement, deliberately: the bar renders visible and this script "arms" it by adding `--armed`,
   which is what lets the CSS hide it. If this file never runs, or the summary section is not on the page, the bar
   keeps its always-visible behaviour rather than disappearing for good - and the matching CSS reserves the page's
   bottom clearance for that case too.

   A plain scroll listener, not IntersectionObserver and not rAF. Both were tried and both left the bar stuck in its
   initial state: this project's headless Chrome fires no IO callbacks at all, and rAF callbacks only run while the
   browser is producing frames, so the deferred measurement never happened. Measuring synchronously on scroll is one
   rect read of already-computed layout, and it is observable - which is the only reason the reveal could be proven
   to work rather than assumed. */

(function () {
  'use strict';

  var ARMED = 'et-cart-sticky-checkout__bar--armed';
  var VISIBLE = 'et-cart-sticky-checkout__bar--visible';

  function setup(root) {
    var bar = root.querySelector('.et-cart-sticky-checkout__bar');
    if (!bar || bar.dataset.etStickyInit === 'true') return;

    /* Which element the shopper has to scroll past is a merchant setting, because "early enough" is a judgement
       about this store's cart, not a constant. The bar duplicates the summary's CTA, so revealing it too early is
       noise and too late loses the running total for a long stretch of page.

         immediately  no anchor at all - the bar is never armed, so it behaves exactly as it did before this script
                      existed. Also the graceful fallback for every case below where the element is missing.
         first_item   past the first line item. Earliest useful point: the shopper has seen what is in the cart.
         all_items    past the whole list.
         summary      past the summary card, so the bar only ever replaces a CTA that has left the screen.

       These all live in OTHER sections, so they are looked up on the document, not inside this section's root. Each
       falls back to the next-latest anchor rather than to nothing, so a missing element delays the reveal instead of
       silently pinning the bar on screen for the whole page. */
    var mode = bar.getAttribute('data-et-scb-reveal') || 'first_item';
    if (mode === 'immediately') return;

    var anchor = null;
    if (mode === 'first_item') {
      anchor = document.querySelector('.et-cart-items__list > .et-cart-line');
    }
    if (!anchor && (mode === 'first_item' || mode === 'all_items')) {
      anchor = document.querySelector('.et-cart-items__list');
    }
    if (!anchor) {
      anchor = document.querySelector('.et-cart-summary__card');
    }
    if (!anchor) return;

    bar.dataset.etStickyInit = 'true';
    bar.classList.add(ARMED);

    var visible = null;

    function measure() {
      /* Show once the anchor's BOTTOM edge has passed the top of the viewport - the anchor is fully behind the
         shopper. Testing the bottom rather than the top is what makes each mode mean "past this thing" rather than
         "level with it". The 1px margin lands the flip on the frame the last pixel leaves, not one frame later. */
      var past = anchor.getBoundingClientRect().bottom < 1;

      if (past === visible) return;

      /* Never hide the bar out from under the keyboard. The --armed rule applies visibility:hidden, which makes a
         focused control unfocusable and drops focus to <body>, losing the user's place (WCAG 2.4.3 Focus Order).
         Scrolling back up with focus parked on the checkout button is exactly that case, so the hide is skipped
         until focus leaves; the next scroll re-evaluates. */
      if (!past && bar.contains(document.activeElement)) return;

      visible = past;
      bar.classList.toggle(VISIBLE, past);
    }

    /* measure() is called straight from the scroll handler rather than deferred into requestAnimationFrame. rAF
       looks like the tidier throttle, but it only runs while the browser is actually producing frames, and a
       deferred callback that never fires leaves the bar stuck in whichever state it started in - which is exactly
       the bug this replaced. One getBoundingClientRect on one element is a read of already-computed layout; it is
       cheaper than the rAF bookkeeping it replaces, and `measure` early-returns before touching the DOM whenever the
       state has not actually flipped. */
    function onScroll() {
      measure();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    /* Set the initial state synchronously - a shopper landing mid-page (back button, anchor link) must not see the
       bar flash in or out on their first scroll. */
    measure();

    root.etStickyTeardown = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      bar.dataset.etStickyInit = 'false';
      bar.classList.remove(ARMED, VISIBLE);
    };
  }

  /* The section wrapper itself carries .et-cart-sticky-checkout (schema `class`), and shopify:section:load hands us
     that very element - querySelectorAll only walks descendants, so the scope has to be tested too. */
  function boot(scope) {
    var root = scope || document;
    if (root.matches && root.matches('.et-cart-sticky-checkout')) setup(root);
    root.querySelectorAll('.et-cart-sticky-checkout').forEach(setup);
  }

  function teardown(scope) {
    var root = scope || document;
    var stop = function (el) {
      if (el.etStickyTeardown) {
        el.etStickyTeardown();
        el.etStickyTeardown = null;
      }
    };
    if (root.matches && root.matches('.et-cart-sticky-checkout')) stop(root);
    root.querySelectorAll('.et-cart-sticky-checkout').forEach(stop);
  }

  if (document.readyState !== 'loading') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  }

  /* Editing the SUMMARY section in the theme editor replaces the anchor element, so rebind every bar rather than
     only the section that fired. */
  document.addEventListener('shopify:section:load', function () {
    teardown(document);
    boot(document);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });
})();
